import { uuidv7 } from '@iobiz/shared'

/**
 * Client HTTP unique de l'application.
 *
 * Deux principes de sécurité :
 *  • Le jeton d'accès vit EN MÉMOIRE, jamais en localStorage — une injection
 *    XSS ne peut donc pas l'exfiltrer.
 *  • Le jeton de rafraîchissement est un cookie httpOnly : le JavaScript de la
 *    page n'y accède pas du tout, d'où `credentials: 'include'`.
 */

export interface FieldError {
  field: string
  code: string
  message: string
}

/** Erreur au format RFC 7807 renvoyée par l'API. */
/**
 * Panne de RÉSEAU, distincte d'une erreur renvoyée par le serveur.
 *
 * ⭐ Sans elle, `fetch` lève une `TypeError` dont le message vaut
 * « Failed to fetch » : les appelants affichent `e.message` tel quel, et le
 * commerçant lit une phrase en anglais qui ne lui dit ni ce qui a échoué, ni
 * quoi faire. Le message est donc fabriqué ICI, une fois : chaque appelant en
 * hérite sans avoir à y penser.
 *
 * ⚠️ Ne concerne QUE les actions exécutées par le serveur — import, export,
 * rattrapage, gestion des accès. Les écritures ordinaires passent par la
 * réplique locale et la file d'attente : elles n'atteignent jamais ce chemin,
 * et continuent de fonctionner sans réseau.
 */
export class HorsLigneError extends Error {
  constructor() {
    super(
      "Action indisponible hors ligne : elle s'exécute sur le serveur. " +
        'Réessayez une fois la connexion revenue.',
    )
    this.name = 'HorsLigneError'
  }
}

/**
 * Distingue une VRAIE panne de réseau d'une annulation volontaire.
 *
 * ⚠️ `fetch` lève aussi quand on annule via `AbortSignal` — ce que
 * l'application fait à chaque frappe dans une recherche. Confondre les deux
 * afficherait « hors ligne » à un utilisateur parfaitement connecté.
 */
function estAnnulation(cause: unknown, signal?: AbortSignal): boolean {
  return signal?.aborted === true || (cause instanceof Error && cause.name === 'AbortError')
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly title: string,
    override readonly message: string,
    readonly errors?: FieldError[],
    readonly requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Message par champ, pour alimenter directement un formulaire. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {}
    for (const e of this.errors ?? []) out[e.field] = e.message
    return out
  }
}

const BASE = '/api/v1'

let accessToken: string | null = null
let onSessionLost: (() => void) | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

/** Appelé quand le rafraîchissement échoue : la session est définitivement perdue. */
export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler
}

/**
 * Identifiant d'appareil, généré une fois et conservé localement.
 * Il servira de clé au moteur de synchronisation (ADR-01) : le serveur s'en
 * sert pour ne pas réexpédier à un appareil les changements qu'il a émis.
 */
const DEVICE_KEY = 'iobiz.deviceId'

export function deviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY)
  if (!id) {
    id = uuidv7()
    localStorage.setItem(DEVICE_KEY, id)
  }
  return id
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: Record<string, unknown> = {}
  try {
    body = (await response.json()) as Record<string, unknown>
  } catch {
    // Réponse non JSON (502 d'un proxy, page d'erreur) : on garde un message générique.
  }
  return new ApiError(
    response.status,
    (body['title'] as string) ?? 'Erreur',
    (body['detail'] as string) ?? `La requête a échoué (${response.status}).`,
    body['errors'] as FieldError[] | undefined,
    body['requestId'] as string | undefined,
  )
}

/**
 * Rafraîchissement à vol unique.
 *
 * Sans cette précaution, cinq requêtes recevant 401 simultanément
 * déclencheraient cinq rotations concurrentes — et la détection de réemploi
 * côté serveur révoquerait la famille de jetons, déconnectant l'utilisateur
 * alors que rien d'anormal ne s'est produit.
 */
/**
 * Issue d'une tentative de rafraîchissement.
 *
 * ⚠️ `refused` et `unreachable` ne se confondent PAS. Le premier signifie que
 * le serveur a dit non — session expirée ou révoquée — et impose de se
 * reconnecter. Le second signifie qu'on n'a parlé à personne : effacer la
 * session dans ce cas déconnecterait un utilisateur légitime pour une simple
 * coupure de WiFi, et lui ferait perdre l'accès à sa réplique locale.
 */
export type RefreshOutcome = 'ok' | 'refused' | 'unreachable'

let refreshing: Promise<RefreshOutcome> | null = null

async function attemptRefresh(): Promise<RefreshOutcome> {
  refreshing ??= (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      if (!response.ok) return 'refused' as const
      const data = (await response.json()) as { accessToken: string }
      accessToken = data.accessToken
      return 'ok' as const
    } catch {
      // `fetch` ne rejette que pour une défaillance réseau : aucun statut n'a
      // été reçu, donc aucun refus n'a été prononcé.
      return 'unreachable' as const
    } finally {
      // Libère le verrou au tick suivant, pour que les appelants en attente
      // lisent bien le résultat avant qu'un nouveau cycle puisse démarrer.
      queueMicrotask(() => {
        refreshing = null
      })
    }
  })()
  return refreshing
}

interface RequestOptions {
  method?: string
  body?: unknown
  /** En-têtes additionnels — sert notamment à If-Match (concurrence optimiste). */
  headers?: Record<string, string>
  /** Interne : empêche une boucle de rafraîchissement infinie. */
  retried?: boolean
  /**
   * N'envoie PAS `X-Device-Id`.
   *
   * ⚠️ Réservé aux écritures en masse côté SERVEUR qui ne passent pas par la
   * réplique locale — l'import, typiquement. Le serveur attribue sinon ces
   * écritures à cet appareil, et le filtre anti-écho refuse ensuite de les lui
   * renvoyer : les lignes existent en base et restent invisibles ici, pour
   * toujours. Le filtre a raison dans le cas général — il évite qu'un appareil
   * se voie renvoyer sa propre saisie — mais l'import n'est pas ce cas.
   */
  sansAppareil?: boolean
  signal?: AbortSignal
}

async function telechargerFichier(path: string, nomFichier: string): Promise<void> {
  const entetes: Record<string, string> = { 'X-Device-Id': deviceId() }
  if (accessToken) entetes['Authorization'] = `Bearer ${accessToken}`

  const demander = async (): Promise<Response> => {
    try {
      return await fetch(`${BASE}${path}`, { headers: entetes, credentials: 'include' })
    } catch {
      // Un export est par nature une opération SERVEUR : sans réseau, il n'y a
      // pas de repli local possible, seulement un message honnête.
      throw new HorsLigneError()
    }
  }

  let reponse = await demander()
  if (reponse.status === 401 && (await attemptRefresh()) === 'ok') {
    if (accessToken) entetes['Authorization'] = `Bearer ${accessToken}`
    reponse = await demander()
  }
  if (!reponse.ok) throw await toApiError(reponse)

  const blob = await reponse.blob()
  const url = URL.createObjectURL(blob)
  const lien = document.createElement('a')
  lien.href = url
  lien.download = nomFichier
  document.body.appendChild(lien)
  lien.click()
  lien.remove()
  // Liberation differee : Firefox annule le telechargement si l'URL est
  // revoquee dans la foulee du clic.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, retried = false, signal } = options

  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers }
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  // Posé sur TOUTES les requêtes, pas seulement celles de synchronisation :
  // le serveur journalise l'appareil émetteur de chaque écriture, y compris
  // celles passées par les routes REST classiques. Sans quoi cet appareil se
  // verrait renvoyer sa propre saisie au cycle suivant.
  if (!options.sansAppareil) headers['X-Device-Id'] = deviceId()

  let response: Response
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      credentials: 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    })
  } catch (cause) {
    if (estAnnulation(cause, signal)) throw cause
    throw new HorsLigneError()
  }

  // 401 : le jeton d'accès a expiré (15 min). On tente une rotation, une seule fois.
  if (response.status === 401 && !retried && !path.startsWith('/auth/')) {
    const issue = await attemptRefresh()
    if (issue === 'ok') {
      return apiFetch<T>(path, { ...options, retried: true })
    }
    // ⚠️ Seul un REFUS ferme la session. Si le réseau tombe entre le 401 et la
    // rotation, aucun refus n'a été prononcé : déconnecter ici mettrait
    // l'utilisateur dehors sans qu'aucun serveur ne l'ait décidé — et hors
    // ligne, il ne pourrait pas se reconnecter. C'est exactement le contraire
    // de ce qu'une application local-first doit faire.
    if (issue === 'unreachable') throw new HorsLigneError()
    accessToken = null
    onSessionLost?.()
    throw await toApiError(response)
  }

  if (!response.ok) throw await toApiError(response)
  if (response.status === 204) return undefined as T

  return (await response.json()) as T
}

export const api = {
  /**
   * ⚠️ `sansAppareil` sert aux pages PUBLIQUES : un visiteur du site vitrine
   * n'est pas un appareil de la boutique, et lui coller l'identifiant du poste
   * qui a ouvert l'onglet n'aurait aucun sens — ni pour la synchronisation, ni
   * pour ce qu'on laisse fuiter à un inconnu.
   */
  get: <T>(path: string, signal?: AbortSignal, options?: { sansAppareil?: boolean }) =>
    apiFetch<T>(path, { ...(signal ? { signal } : {}), ...(options ?? {}) }),
  /**
   * ⚠️ `options` accepte `sansAppareil` pour les traitements EN MASSE côté
   * serveur — import, rattrapage de codes-barres. Ces écritures ne passent pas
   * par la réplique locale : attribuées à cet appareil, le filtre anti-écho
   * refuserait ensuite de les lui renvoyer, et le résultat resterait invisible
   * ici alors qu'il existe en base.
   */
  post: <T>(path: string, body?: unknown, options?: { sansAppareil?: boolean }) =>
    apiFetch<T>(path, { method: 'POST', body, ...(options ?? {}) }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PATCH', body }),
  /** REMPLACE la ressource — utilisé là où un envoi partiel n'aurait pas de
   *  sens, comme le jeu de permissions d'un rôle : les cases décochées
   *  doivent disparaître, ce qu'un PATCH ne dirait pas. */
  put: <T>(path: string, body?: unknown, options?: { sansAppareil?: boolean }) =>
    apiFetch<T>(path, { method: 'PUT', body, ...(options ?? {}) }),
  /**
   * Telechargement d'un fichier engendre par l'API.
   *
   * ⚠️ NE PAS utiliser `window.open` : une navigation classique n'emporte pas
   * l'en-tete `Authorization`, et le jeton d'acces vit en MEMOIRE, pas dans un
   * cookie. Le serveur repond alors « Authentification requise » sur un export
   * parfaitement legitime — le defaut est invisible en developpement si l'on
   * teste avec une session par cookie.
   */
  telecharger: (path: string, nomFichier: string) => telechargerFichier(path, nomFichier),
  delete: <T>(path: string, options?: { sansAppareil?: boolean }) =>
    apiFetch<T>(path, { method: 'DELETE', ...(options ?? {}) }),
  /** Tente de restaurer une session au démarrage, depuis le cookie httpOnly.
   *  Renvoie l'ISSUE et non un booléen : l'appelant doit pouvoir distinguer
   *  un refus d'une absence de réseau. */
  restore: attemptRefresh,
}
